import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, MessageSquare } from "lucide-react";

import { AnnouncementReactions } from "@/components/dashboard/announcement-reactions";
import { avatarTintStyle, initialsOf } from "@/lib/dashboard/people";
import { cn } from "@/lib/utils";

// How long the summary stays tinted after a comment lands on a post you have collapsed.
const BUMP_MS = 1500;

/**
 * The facepile behind a collapsed thread: who has been talking, without fetching what they
 * said. The server sends at most three, newest commenter first.
 *
 * The left padding cancels the first avatar's negative margin, so the row starts flush while
 * the rest overlap.
 */
function Facepile({ authors }) {
  if (!authors.length) return null;
  return (
    <div className="flex pl-[7px]" aria-hidden>
      {authors.map((author, index) => (
        // The ring is the surface punched out from behind each avatar, so it has to BE the
        // post's fill — any other token draws a visible outline instead of a clean gap.
        <div
          key={`${author.id}-${index}`}
          title={author.name}
          className="-ml-[7px] grid h-[22px] w-[22px] place-items-center rounded-full border-2 border-card-raised text-[9px] font-bold"
          style={avatarTintStyle(author.id)}
        >
          {initialsOf(author.name)}
        </div>
      ))}
    </div>
  );
}

/**
 * The bar under every announcement: reactions on the left, the comment summary on the right.
 *
 * The summary is also the thread's toggle, and it carries its own state in its shape — a
 * facepile and "last 3h ago" when the thread is folded away, a bare count and an up-chevron
 * when it is open, and a plain "Comment" invitation when nobody has said anything yet. That
 * last one skips straight to the composer: there is nothing to expand INTO otherwise.
 */
export function AnnouncementSocialBar({
  reactions,
  onToggleReaction,
  commentCount,
  lastCommentAt,
  commentAuthors,
  expanded,
  onToggleExpanded,
  onStartComment,
  formatRelativeTime,
}) {
  const [bumped, setBumped] = useState(false);
  const previousCountRef = useRef(commentCount);

  // A comment arriving on a post you have collapsed is the one update you would otherwise
  // miss entirely — the count changes by one digit in the corner. Tint it briefly so the
  // change is something you notice rather than something you have to spot.
  useEffect(() => {
    const grew = commentCount > previousCountRef.current;
    previousCountRef.current = commentCount;
    if (!grew || expanded) return undefined;
    setBumped(true);
    const timer = setTimeout(() => setBumped(false), BUMP_MS);
    return () => clearTimeout(timer);
  }, [commentCount, expanded]);

  const label = `${commentCount} ${commentCount === 1 ? "comment" : "comments"}`;
  const lastAgo = formatRelativeTime?.(lastCommentAt) || "";

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t border-border pt-[11px]">
      <AnnouncementReactions reactions={reactions} onToggle={onToggleReaction} />

      <span className="flex-1" />

      {commentCount === 0 ? (
        <button
          type="button"
          onClick={onStartComment}
          className="inline-flex items-center gap-2 text-[12.5px] font-bold text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          <MessageSquare className="h-[13px] w-[13px]" aria-hidden />
          Comment
        </button>
      ) : (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          title={expanded ? "Hide comments" : "Show comments"}
          className="inline-flex items-center gap-2 text-[12.5px] font-bold transition-colors duration-150"
        >
          {!expanded && <Facepile authors={commentAuthors} />}
          <span className="text-primary">{label}</span>
          {!expanded && lastAgo && (
            <span
              className={cn(
                "text-[12px] font-normal transition-colors duration-150",
                bumped ? "text-primary" : "text-muted-foreground",
              )}
            >
              · last {lastAgo}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-[13px] w-[13px] text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown
              className={cn(
                "h-[13px] w-[13px] transition-colors duration-150",
                bumped ? "text-primary" : "text-muted-foreground",
              )}
              aria-hidden
            />
          )}
        </button>
      )}
    </div>
  );
}
